// PRD 10 §4.2 — content list table. Columns: EID / image / title / preview /
// category / status·date / home-exposure / delete. The category column is ALWAYS
// shown (v2.1); 홈 노출 was added by v2.2 and finalised by v2.4 (SHCT-486).
//
// Sort headers become clickable only when `onSort` is provided (wired in
// Slice 3); row-open and delete are wired in Slices 5 / 7. Until then the
// table renders read-only.

import { useTranslation } from 'react-i18next';

import type {
  ContentSortKey,
  ContentSummary,
  SortDir,
} from '../types/health-encyclopedia.types';
import { EncyclopediaListRow } from './EncyclopediaListRow';

interface EncyclopediaTableProps {
  rows: ContentSummary[];
  sortKey: ContentSortKey;
  sortDir: SortDir;
  canDelete: boolean;
  // PRD §3.9 — CONTENT_LIB:WRITE gates the home-exposure toggle (READ can see it).
  canWrite: boolean;
  // Library-wide ON count; once it reaches maxHomeExposed every unchecked toggle goes disabled.
  homeExposedCount: number;
  // The cap the server enforces, served in the list response rather than hardcoded here.
  maxHomeExposed: number;
  // Set while a toggle request is in flight, so the row can lock that one switch.
  pendingHomeExposeEid?: string | null;
  onSort?: (key: ContentSortKey) => void;
  onRowOpen?: (eid: string) => void;
  onDelete?: (content: ContentSummary) => void;
  onHomeExposeChange?: (content: ContentSummary, next: boolean) => void;
}

// Maps a column's sort key to the displayed sort glyph. '↕' = inactive/sortable.
function sortGlyph(active: boolean, dir: SortDir): string {
  if (!active) return '↕';
  return dir === 'asc' ? '↑' : '↓';
}

export function EncyclopediaTable({
  rows,
  sortKey,
  sortDir,
  canDelete,
  canWrite,
  homeExposedCount,
  maxHomeExposed,
  pendingHomeExposeEid,
  onSort,
  onRowOpen,
  onDelete,
  onHomeExposeChange,
}: EncyclopediaTableProps) {
  const { t } = useTranslation('health-encyclopedia');

  const renderHeader = (
    key: ContentSortKey,
    label: string,
    extra?: React.ThHTMLAttributes<HTMLTableCellElement>,
  ) => {
    if (!onSort) {
      return <th {...extra}>{label}</th>;
    }
    return (
      <th
        {...extra}
        style={{ cursor: 'pointer', ...extra?.style }}
        onClick={() => onSort(key)}
      >
        {label} <span className="sort-icon">{sortGlyph(sortKey === key, sortDir)}</span>
      </th>
    );
  };

  return (
    // SHCT-606 — fixed layout so a long title/preview stays inside its cell
    // instead of stretching the column and pushing the table past its card.
    // 제목 and 미리보기 are the two unpinned columns; they share the remainder.
    <table className={`table table--fixed${onRowOpen ? ' table--clickable' : ''}`}>
      <thead>
        <tr>
          {renderHeader('eid', t('list.columns.eid'), { style: { width: 90 } })}
          <th style={{ width: 80 }}>{t('list.columns.image')}</th>
          {renderHeader('title', t('list.columns.title'))}
          <th>{t('list.columns.preview')}</th>
          {renderHeader('category', t('list.columns.category'), { style: { width: 140 } })}
          {renderHeader('publishedAt', t('list.columns.statusDate'), { style: { width: 140 } })}
          {/* PRD §4.2 — 홈 노출 is not sortable (정렬 가능 ❌). */}
          <th style={{ width: 80, textAlign: 'center' }}>{t('list.columns.homeExposed')}</th>
          <th style={{ width: 60, textAlign: 'center' }}>{t('list.columns.delete')}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((content) => (
          <EncyclopediaListRow
            key={content.eid}
            content={content}
            canDelete={canDelete}
            canWrite={canWrite}
            homeExposedCount={homeExposedCount}
            maxHomeExposed={maxHomeExposed}
            isHomeExposePending={pendingHomeExposeEid === content.eid}
            onRowOpen={onRowOpen}
            onDelete={onDelete}
            onHomeExposeChange={onHomeExposeChange}
          />
        ))}
      </tbody>
    </table>
  );
}
